import React, { useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Alert, Upload } from 'antd';
import {
  ArrowUpTrayIcon,
  XMarkIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline';
import { TWButton } from '../../../components/ui';
import storeApi from '../../../services/storeApi';
import menuApi from '../../../services/menuApi';
import menuImportApi from '../../../services/menuImportApi';
import squareCatalogApi from '../../../services/squareCatalogApi';
import oauthApi from '../../../services/oauthApi';
import { OAuthProviderType } from '../../../data/enums';
import useSquareIntegrated from '../../../components/menu/square-shared/use-square-integrated';
import squareErrorToast from '../../../components/menu/square-shared/squareErrorToast';
import findUnpublishedSquareDraft from '../../../components/menu/square-shared/square-draft';
import SquareIcon from '../../../assets/square.svg';
import { validateUploadFile, MB } from '../../../utils/fileValidation';
import '../menu-import.css';

const ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
const MAX_BYTES = 20 * MB;

const UploadStep = ({ companyId, onUploaded, onSquareImportStarted, onError }) => {
  const navigate = useNavigate();
  const [source, setSource] = useState('upload'); // 'upload' | 'square'

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileError, setFileError] = useState('');
  const [storeId, setStoreId] = useState('');
  const [stores, setStores] = useState([]);
  const [loadingStores, setLoadingStores] = useState(false);
  const [uploading, setUploading] = useState(false);

  const { integrated: squareIntegrated, checked: squareChecked } = useSquareIntegrated();
  const rolepermissions = useSelector((state) => state.authReducer.rolepermissions);
  const canImportSquare = !!rolepermissions?.includes('squarecatalog-import');
  const [squareLocations, setSquareLocations] = useState([]);
  const [loadingSquareLocations, setLoadingSquareLocations] = useState(false);
  const [squareStoreId, setSquareStoreId] = useState('');
  const [squareImporting, setSquareImporting] = useState(false);
  const [existingSquareDraft, setExistingSquareDraft] = useState(null);
  // Guards getLocationList to fire at most once per company, lazily, only
  // once the user actually picks the Square source card — not on page load.
  // Tracks the companyId already fetched (rather than a plain boolean) so
  // switching companies still triggers a fresh fetch.
  const squareLocationsFetchedForRef = useRef(null);
  // Mirrors squareLocationsFetchedForRef: guards the unpublished-draft check
  // to fire at most once per company, lazily, alongside the location fetch.
  const draftCheckedForCompanyRef = useRef(null);

  React.useEffect(() => {
    if (!companyId) return;
    const loadStores = async () => {
      try {
        setLoadingStores(true);
        const res = await storeApi.list(`?companyid=${companyId}&PageSize=1000`);
        setStores(res?.data?.data || []);
      } catch {
        setStores([]);
      } finally {
        setLoadingStores(false);
      }
    };
    loadStores();
  }, [companyId]);

  // Same shape/filtering as SquareMenuTools.integratedStores: only stores
  // that are paired with an integrated Square location can be import targets.
  // Lazy: only fetch once the user has actually picked the Square source
  // card, and only once per mount (not on every card re-selection).
  React.useEffect(() => {
    if (!companyId || !squareIntegrated || source !== 'square') {
      return undefined;
    }
    let cancelled = false;

    if (squareLocationsFetchedForRef.current !== companyId) {
      squareLocationsFetchedForRef.current = companyId;
      const loadLocations = async () => {
        try {
          setLoadingSquareLocations(true);
          const list = await oauthApi.getLocationList({
            CompanyId: companyId,
            Type: OAuthProviderType.Square,
          });
          if (!cancelled) setSquareLocations(list?.data || []);
        } catch {
          if (!cancelled) setSquareLocations([]);
        } finally {
          if (!cancelled) setLoadingSquareLocations(false);
        }
      };
      loadLocations();
    }

    if (draftCheckedForCompanyRef.current !== companyId) {
      draftCheckedForCompanyRef.current = companyId;
      const loadDraftCheck = async () => {
        try {
          const res = await menuApi.list(`?companyid=${companyId}`);
          if (!cancelled) setExistingSquareDraft(findUnpublishedSquareDraft(res.data.data));
        } catch {
          if (!cancelled) setExistingSquareDraft(null);
        }
      };
      loadDraftCheck();
    }

    return () => {
      cancelled = true;
    };
  }, [companyId, squareIntegrated, source]);

  const integratedStores = stores.filter((store) =>
    squareLocations.some((loc) => loc.storeId === store.id && loc.isIntegrated)
  );
  const effectiveSquareStoreId =
    squareStoreId || (integratedStores.length === 1 ? integratedStores[0].id : '');

  const acceptFile = (picked) => {
    setFile(picked);
    if (picked.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(picked);
    } else {
      setPreview(null);
    }
  };

  // Gate every file (drag-drop or click pick) through the same validation.
  // Errors are shown inline (persistent) instead of a toast; always return a
  // falsy/IGNORE value so antd never auto-uploads.
  const beforeUpload = (picked) => {
    const result = validateUploadFile(picked, { accept: ACCEPT, maxBytes: MAX_BYTES });
    if (!result.ok) {
      setFileError(
        result.reason === 'size'
          ? 'File is too large — max 20 MB.'
          : 'Use JPEG, PNG, WEBP or PDF.',
      );
      return Upload.LIST_IGNORE;
    }
    setFileError('');
    acceptFile(picked);
    return false;
  };

  // Remove the picked file. stopPropagation/preventDefault so the click does
  // not bubble up to the Dragger and reopen the file picker.
  const removeFile = (e) => {
    e.stopPropagation();
    e.preventDefault();
    setFile(null);
    setPreview(null);
    setFileError('');
  };

  const handleUpload = async () => {
    if (!file || !companyId) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('companyid', companyId);
      if (storeId) formData.append('storeid', storeId);
      const res = await menuImportApi.upload(formData);
      const jobId = res?.data?.data?.jobid;
      if (!jobId) throw new Error('Upload failed');
      onUploaded(jobId);
    } catch (err) {
      onError(err?.response?.data || err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSquareImport = async () => {
    if (!effectiveSquareStoreId || !companyId) return;
    setSquareImporting(true);
    try {
      const res = await squareCatalogApi.importMenu({
        companyid: companyId,
        storeid: effectiveSquareStoreId,
      });
      const jobId = res?.data?.data?.jobid;
      if (!jobId) throw new Error('Square import failed');
      onSquareImportStarted(jobId);
    } catch (err) {
      squareErrorToast(err);
    } finally {
      setSquareImporting(false);
    }
  };

  const isPdf = file?.type === 'application/pdf';
  const squareCardDisabled = !canImportSquare || !squareChecked || !squareIntegrated;
  // Only nudge the user to connect Square when permission isn't the blocker —
  // users without squarecatalog-import shouldn't be told to go connect Square.
  const showSquareHint = canImportSquare && squareChecked && !squareIntegrated;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setSource('upload')}
          aria-pressed={source === 'upload'}
          className={`flex items-start gap-3 rounded-xl border p-4 text-left shadow-sm transition ${
            source === 'upload'
              ? 'border-blue-600 ring-2 ring-blue-600 bg-white dark:bg-slate-800'
              : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600'
          }`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-500 dark:bg-sky-500/10 dark:text-sky-400">
            <ArrowUpTrayIcon className="h-5 w-5" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-slate-900 dark:text-white">
              Upload file
            </span>
            <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
              Photo or PDF of your menu
            </span>
          </span>
        </button>

        <button
          type="button"
          onClick={() => !squareCardDisabled && setSource('square')}
          disabled={squareCardDisabled}
          aria-pressed={source === 'square'}
          className={`flex items-start gap-3 rounded-xl border p-4 text-left shadow-sm transition ${
            squareCardDisabled
              ? 'cursor-not-allowed border-slate-200 bg-slate-50 opacity-60 dark:border-slate-700 dark:bg-slate-800/60'
              : source === 'square'
                ? 'border-blue-600 ring-2 ring-blue-600 bg-white dark:bg-slate-800'
                : 'border-slate-200 bg-white hover:border-slate-300 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-slate-600'
          }`}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-700">
            <img src={SquareIcon} alt="" className="h-5 w-5" />
          </span>
          <span>
            <span className="block text-sm font-semibold text-slate-900 dark:text-white">
              Import from Square
            </span>
            <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
              {showSquareHint
                ? 'Connect Square in Settings → Integrations first.'
                : 'Pull your live catalog as a draft menu'}
            </span>
          </span>
        </button>
      </div>

      {source === 'upload' ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Target store (optional)
            </label>
            <select
              value={storeId}
              onChange={(e) => setStoreId(e.target.value)}
              disabled={loadingStores}
              className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
            >
              <option value="">All stores in company</option>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-6">
            <Upload.Dragger
              accept={ACCEPT}
              multiple={false}
              maxCount={1}
              showUploadList={false}
              beforeUpload={beforeUpload}
              className="menu-import-dragger"
            >
              <div className="group flex flex-col items-center justify-center px-6 py-12">
                {file ? (
                  <div className="relative flex w-full items-center justify-center">
                    <button
                      type="button"
                      onClick={removeFile}
                      aria-label="Remove file"
                      className="absolute -right-2 -top-2 z-10 rounded-full bg-white p-1 text-slate-500 shadow ring-1 ring-slate-200 transition hover:text-rose-600 dark:bg-slate-700 dark:text-slate-300 dark:ring-slate-600"
                    >
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                    {isPdf ? (
                      <span className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200">
                        <DocumentTextIcon className="h-5 w-5 shrink-0 text-rose-500" />
                        <span className="max-w-[16rem] truncate">{file.name}</span>
                      </span>
                    ) : (
                      <img
                        src={preview}
                        alt="Preview"
                        className="max-h-48 rounded-md object-contain"
                      />
                    )}
                  </div>
                ) : (
                  <>
                    <span className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-50 text-blue-500 transition motion-reduce:transition-none group-hover:bg-blue-100 dark:bg-sky-500/10 dark:text-sky-400 dark:group-hover:bg-sky-500/20">
                      <ArrowUpTrayIcon className="h-6 w-6" />
                    </span>
                    <span className="mt-3 text-base font-semibold text-slate-700 dark:text-slate-100">
                      Drop your menu here, or click to browse
                    </span>
                    <span className="mt-1 text-xs text-slate-400">
                      JPEG, PNG, WEBP or PDF · 20 MB
                    </span>
                  </>
                )}
              </div>
            </Upload.Dragger>

            {!file ? (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-400">Works with</span>
                {['Printed menu photo', 'Screenshot', 'PDF export'].map((tip) => (
                  <span
                    key={tip}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                  >
                    {tip}
                  </span>
                ))}
              </div>
            ) : null}

            {fileError ? (
              <p className="mt-2 flex items-center gap-1.5 text-sm text-rose-600 dark:text-rose-400">
                <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
                {fileError}
              </p>
            ) : null}
          </div>

          <div className="mt-6 flex justify-end">
            <TWButton
              variant="primary"
              disabled={!file || uploading}
              loading={uploading}
              onClick={handleUpload}
            >
              Upload &amp; Analyze
            </TWButton>
          </div>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
              Square store
            </label>
            {loadingSquareLocations ? (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Loading integrated stores…
              </p>
            ) : integratedStores.length > 1 ? (
              <select
                value={squareStoreId}
                onChange={(e) => setSquareStoreId(e.target.value)}
                className="mt-1 block w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-700 dark:text-white"
              >
                <option value="">Select a store</option>
                {integratedStores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            ) : integratedStores.length === 1 ? (
              <p className="mt-1 text-sm text-slate-700 dark:text-slate-200">
                {integratedStores[0].name}
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                No stores are integrated with Square yet.
              </p>
            )}
          </div>

          {existingSquareDraft && (
            <Alert
              className="mt-6"
              type="info"
              showIcon
              message={`You already have an unpublished Square draft: "${existingSquareDraft.name}".`}
              action={
                <TWButton
                  onClick={() => navigate(`/dashboard/menus?menuid=${existingSquareDraft.id}`)}
                >
                  Open draft
                </TWButton>
              }
            />
          )}

          <div className="mt-6 flex justify-end">
            <TWButton
              variant="primary"
              disabled={!effectiveSquareStoreId || squareImporting}
              loading={squareImporting}
              onClick={handleSquareImport}
            >
              Import from Square
            </TWButton>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadStep;
